import { FC, useContext } from "react";
import { Route, Routes } from "react-router-dom";
import Home from "src/modules/Home";
import { AppContext } from "src/api/AppContext";
import { UserRole } from "src/generated/graphql";
import ClientDashboard from "src/modules/Home/ClientDashboard";
import EmployeeDashboard from "src/modules/Home/EmployeeDashboard";
import MyProfile from "src/modules/MyProfile";
import OrganizationList from "src/modules/OrganizationList";
import OrganizationDetails from "src/modules/OrganizationDetails";
import LocationList from "src/modules/Location/LocationList";
import { ROUTES } from "src/utils/constants";
import LocationDetail from "src/modules/Location/Detail";
import PolicyList from "src/modules/Policy/PolicyList";
import PolicyDetail from "src/modules/Policy/PolicyDetail";
import EmployeeList from "src/modules/Employee/EmployeeList";
import EmployeeDetail from "src/modules/Employee/EmployeeDetail";
import RoomList from "src/modules/Room/List";
import ServiceDetail from "src/modules/Service/ServiceDetail";
import TimeClockList from "src/modules/TimeClock/TimeClockList";
import DiscountsList from "src/modules/Discounts/List";
import AgreementList from "src/modules/Agreement/List";
import AgreementDetail from "src/modules/Agreement/Detail";
import PackageDetail from "src/modules/Package/Detail";
import PackageList from "src/modules/Package/List";
import DiscountsDetails from "src/modules/Discounts/Details";
import ProductGroupList from "src/modules/ProductGroup/List";
import ProductGroupDetail from "src/modules/ProductGroup/Detail";
import ClientDetail from "src/modules/Client/ClientDetail";
import Calendar from "src/modules/Calendar";
import Dashboard from "src/modules/Dashboard";
import RevenueReport from "src/modules/Dashboard/Revenue/RevenueReport";
import POSReport from "src/modules/Dashboard/PointOfSale/POSReport";
import AgreementReport from "src/modules/Dashboard/Agreements/AgreementReport";
import AccountsManagement from "src/modules/Client";
import GuestDetail from "src/modules/Client/Guest/GuestDetail";
import { DashboardContextProvider } from "src/api/context/DashboardContext";
import Requests from "src/modules/Requests";
import LeadDetail from "src/modules/Client/Lead/LeadDetail";
import CalendarRecurringEvents from "src/modules/CalendarRecurringEvents";
import Services from "src/modules/Service";
import PrivateSessionDetail from "src/modules/Service/PrivateSession/PrivateSessionDetail";
import ServiceList from "src/modules/Service/ServiceList";
import Stations from "src/modules/Stations";
import Roles from "src/modules/Roles";
import LocationServiceDetail from "src/modules/LocationService/LocationServiceDetail";
import LocationPackageList from "src/modules/Package/LocationPackageList";
import SignNowPostMessage from "src/modules/Policy/PolicyEditor/SignNowPostMessage";
import Reports from "src/modules/Reports";

type RouteDetails = {
  path: string;
  element: JSX.Element;
  roles: UserRole[];
};

const ClubRouter: FC = () => {
  const { currentEmployee, currentUser } = useContext(AppContext);

  const routes: RouteDetails[] = [
    {
      path: "/home",
      element: <Home />,
      roles: [],
    },
  ];

  return (
    <Routes>
      <Route element={<DashboardContextProvider />}>
        <Route path="/" element={<Home />} />
        <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
        <Route path={ROUTES.REPORT_REVENUE} element={<RevenueReport />} />
        <Route path={ROUTES.REPORT_POS} element={<POSReport />} />
        <Route path={ROUTES.REPORT_AGREEMENT} element={<AgreementReport />} />
        <Route path={ROUTES.REPORT_PACKAGE} element={<POSReport />} />
      </Route>
      <Route path={ROUTES.REPORTS} element={<Reports />} />
      <Route path="/home" element={<Home />} />
      <Route
        path="/specific-dashboard"
        element={
          currentUser?.role == UserRole.Client ? (
            <ClientDashboard />
          ) : (
            <EmployeeDashboard />
          )
        }
      />
      <Route path="/profile" element={<MyProfile />} />
      <Route path={ROUTES.ORGANIZATION} element={<OrganizationList />} />
      <Route path={ROUTES.ORGANIZATION_ID} element={<OrganizationDetails />} />

      <Route path={ROUTES.LOCATION} element={<LocationList />} />
      <Route path={ROUTES.LOCATION_ID} element={<LocationDetail />} />

      <Route path={ROUTES.POLICY} element={<PolicyList />} />
      <Route path={ROUTES.POLICY_ID} element={<PolicyDetail />} />
      <Route path={"/sign-now-close"} element={<SignNowPostMessage />} />

      <Route path={ROUTES.EMPLOYEE} element={<EmployeeList />} />
      <Route path={ROUTES.EMPLOYEE_ID} element={<EmployeeDetail />} />

      <Route path={ROUTES.ROOM} element={<RoomList />} />
      <Route path={ROUTES.LOCATION_SERVICE} element={<Services />} />
      <Route
        path={ROUTES.LOCATION_SERVICE_ID}
        element={<LocationServiceDetail />}
      />
      <Route path={ROUTES.SERVICE} element={<Services />} />
      <Route
        path={ROUTES.SERVICE_ID}
        element={<ServiceDetail isOrg={true} />}
      />
      <Route
        path={ROUTES.LOCATION_SERVICE_ID}
        element={<ServiceDetail isOrg={false} />}
      />
      <Route
        path={ROUTES.LOCATION_SESSION_ID}
        element={<PrivateSessionDetail isLocationSession={true} />}
      />
      <Route
        path={ROUTES.SESSION_ID}
        element={<PrivateSessionDetail isLocationSession={false} />}
      />

      <Route path={ROUTES.TIME_CLOCK} element={<TimeClockList />} />
      <Route path={ROUTES.STATIONS} element={<Stations />} />
      <Route path={ROUTES.REQUESTS} element={<Requests />} />

      <Route path={ROUTES.DISCOUNTS} element={<DiscountsList />} />
      <Route path={ROUTES.CLIENT} element={<AccountsManagement />} />
      <Route path={ROUTES.CLIENT_ID} element={<ClientDetail />} />
      <Route path={ROUTES.GUEST_ID} element={<GuestDetail />} />

      <Route path={ROUTES.LEAD_ID} element={<LeadDetail />} />

      <Route
        path={ROUTES.ORG_AGREEMENT}
        element={<AgreementList isOrg={true} />}
      />
      <Route path={ROUTES.ROLES} element={<Roles />} />

      <Route path={ROUTES.ORG_PACKAGE} element={<PackageList isOrg={true} />} />
      <Route
        path={ROUTES.ORG_PACKAGE_ID}
        element={<PackageDetail isOrg={true} />}
      />

      <Route
        path={ROUTES.ORG_AGREEMENT_ID}
        element={<AgreementDetail isOrg={true} />}
      />
      <Route path={ROUTES.AGREEMENT} element={<AgreementList />} />
      <Route
        path={ROUTES.AGREEMENT_ID}
        element={<AgreementDetail isOrg={false} />}
      />

      <Route path={ROUTES.PACKAGE} element={<PackageList isOrg={false} />} />
      <Route
        path={ROUTES.PACKAGE_ID}
        element={<PackageDetail isOrg={false} />}
      />

      <Route path={ROUTES.CALENDER} element={<Calendar />} />
      <Route
        path={ROUTES.CALENDER_RECURRING_EVENT}
        element={<CalendarRecurringEvents />}
      />

      <Route path={ROUTES.DISCOUNTS_ID} element={<DiscountsDetails />} />

      <Route path={ROUTES.PRODUCT} element={<ProductGroupList />} />
      <Route path={ROUTES.PRODUCT_ID} element={<ProductGroupDetail />} />
    </Routes>
  );
};

export default ClubRouter;
